/* @module csi/widget/MessageDialog */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/on",
	"dijit/form/Button",
	"dijit/ProgressBar",
	"dijit/registry",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/widget/Dialog"
], function(declare, lang, domClass, domConstruct, on, Button, ProgressBar, registry, _, html, locale, Dialog) {

	var _BUTTON_PRIMARY = "__primary";
	var _BUTTON_CANCEL = "C";

	var MessageDialog = declare(Dialog, {
		// summary:
		//		Pops up a dialog for user response with specified message, buttons, and optional icon;
		//		Button keys available by default are below, but they can be any string.
		//			OK: OK
		//			Y: Yes
		//			N: No
		//			C: Cancel
		//			CONT: Continue

		// Button: First button in the list
		confirmButton: null,

		destroyOnHide: true,

		// Boolean: show exception message immediately
		bShowException: false,

		// String[]: array of strings identifying available buttons
		buttons: null,

		// String: server exception to display along with error
		exception: null,

		// Font awesome icon name, if used
		fontAwesomeIcon: null,

		// String: CSS class to display icon
		iconClass: null,

		// boolean: initially disable confirm button
		bInitiallyDisableConfirmButton: false,

		// Material icon name, if used material icons
		materialIcon: null,

		// message to display to user
		message: null,

		// ProgressBar: progress bar widget if displaying progress
		progressBar: null,

		// Number: percentage value from 0-1 if determinate; set to Infinity if indeterminate
		progressValue: null,

		// extension point for handling response - response will be key of button that was clicked ("C" if dialog closed)
		// onResponse: function(/*String*/key, /*Object*/values) { },

		// extension point for populating message if string is not enough
		// onMessage: function(/*DOMNode*/messageNode) { },

		onCreate: function() {
			// summary: populate dialog with icon, message, and buttons
			var divContent = domConstruct.create("div", null, this.containerNode);
			var divMessage = domConstruct.create("div", {"class": "csi-message-txt"}, divContent);

			domClass.add(this.domNode, "csiMessage");

			// add icon
			if (this.iconClass) {
				domConstruct.create("div", {
					"class": "csiActionIcon " + this.iconClass,
					"style": "vertical-align: middle"
				}, divMessage, "before");
			}

			// add material icon
			if (this.materialIcon) {
				domConstruct.create("div", {
					"class": "csi-message-icon material-icons csi-material-icon-" + this.materialIcon,
					innerHTML: this.materialIcon
				}, divMessage, "before");
			}

			// add font awesome
			if (this.fontAwesomeIcon) {
				domConstruct.create("div", {
					"class": "csi-message-icon " + this.fontAwesomeIcon
				}, divMessage, "before");
			}

			// add message
			if (this.message) {
				html.multiLineText(divMessage, this.message);
			}

			if (this.onMessage) {
				this.onMessage(divMessage);
			}

			// add progress bar
			if (this.progressValue != null) {
				this.progressBar = new ProgressBar({
					"class": (this.progressValue == Infinity) ? "" : "csi-message-progress-determinate",
					maximum: 1,
					value: this.progressValue
				}).placeAt(domConstruct.create("div", {"class": "csi-message-progress"}, divContent));
				this.progressBar.startup();
			}

			// add exception UI
			if (this.exception) {
				if (this.bShowException) {
					this._showException(divContent, "after");
				} else {
					var divException = domConstruct.create("div", {"class": "csi-message-exception-link"}, divContent, "after");
					var a = html.a({
						"class": html.CSS_BROWSE,
						innerText: locale.resource("ShowException")
					}, divException);

					this.own(on(a, "click", lang.hitch(this, this._onException)));
				}
			}

			// add buttons
			if (this.buttons) {
				var divActionsNode = domConstruct.create("div", {"class": "csiActionsNode"}, this.containerNode);
				var divButtons = domConstruct.create("div", {"class": "csiActions"}, divActionsNode);
				var btnTabIndex = 2;
				var forcePrimary = this.buttons && (this.buttons.length == 1) && this.buttons[0] != MessageDialog.BUTTON_OK && this.buttons[0] != MessageDialog.BUTTON_CLOSE;

				_.each(this.buttons, function(key) {
					var currBtnTabIndex = btnTabIndex++;
					var btnClass = 'csi-secondary-btn';
					if (key == _BUTTON_CANCEL) {
						btnClass = 'csi-link-btn';
					}
					if (forcePrimary || key.indexOf(_BUTTON_PRIMARY) >= 0) {
						btnClass = 'csi-primary-btn';
						key = key.replace(_BUTTON_PRIMARY, '');
						currBtnTabIndex = 1;
					}

					var button = new Button({
						label: _.escape(locale.resource("BUTTON_" + key)),
						"class": btnClass,
						buttonKey: key
					}, domConstruct.create("button", null, divButtons));
					if (!this.confirmButton) {
						this.confirmButton = button;
						if (this.bInitiallyDisableConfirmButton) {
							html.setDisabled(button, true);
						}
					}
					this.connect(button, "onClick", "onButton");
					button.startup();
					button.focusNode.tabIndex = currBtnTabIndex;
				}, this);
			}
		},

		onButton: function(/*Event*/e) {
			// summary: respond to button click
			if (this.onResponse) {
				this.onResponse(registry.getEnclosingWidget(e.currentTarget).buttonKey, this.get("value"), e);
			}

			this.hide();
		},

		onCancel: function() {
			// summary: respond to dialog being closed
			if (this.onResponse) {
				this.onResponse("C", this);
			}
		},

		_onException: function(/*Event*/e) {
			// summary: toggle display of exception
			this._showException(e.target.parentNode, "after");
			domConstruct.destroy(e.target.parentNode);

			// resize twice because Dojo positions the dialog before resizing it, so in some cases the first time does not fully work
			this.resize();
			this.resize();
		},

		_showException: function(/*DOMNode*/domNodeBefore, /*String?*/pos) {
			// summary: display exception stack trace
			html.create("pre", {innerText: this.exception}, html.create("div", {"class": "csi-message-exception"}, domNodeBefore, pos));
		},

		/**
		* Return the action buttons on a dialog.
		* @return {Button[]} - array of buttons
		*/
		getAllButtons: function() {
			return _.filter(registry.findWidgets(this.domNode), function(widget) {
				return widget.type === "button";
			});
		},

		/**
		* If button key is present and valid, returns only a button that match the key.
		* @param {string} key - button key
		* @return {Button} - button
		*/
		getActionButton: function(key) {
			return _.findWhere(this.getAllButtons(), {buttonKey: key});
		}
	});

	// common constants
	lang.mixin(MessageDialog, {
		// Button type
		BUTTON_OK: "OK",
		BUTTON_YES: "Y",
		BUTTON_NO: "N",

		BUTTON_AGREE: "AGREE",
		BUTTON_APPLY: "APPLY",
		BUTTON_CANCEL: _BUTTON_CANCEL,
		BUTTON_CHANGE: "CHANGE",
		BUTTON_CHANGE_TEMPLATE: "CHANGE_TEMPLATE",
		BUTTON_CHECK_PROGRESS:"CHECK_PROGRESS",
		BUTTON_CLEAR: "CLEAR",
		BUTTON_CLOSE: "CLOSE",
		BUTTON_CONFIRM: "CONFIRM",
		BUTTON_CONTINUE: "CONT",
		BUTTON_CONTWOCOPY: "CONTWOCOPY",
		BUTTON_COPYCANVAS: "COPYCANVAS",
		BUTTON_CONVERT: "CONVERT",
		BUTTON_DELETE: "DELETE",
		BUTTON_DISABLE_ROLE: "DISABLE_ROLE",
		BUTTON_DISCARD: "DISCARD",
		BUTTON_EDIT_SIZE_MAP: "EDIT_SIZE_MAP",
		BUTTON_EXPAND_ANYWAY: "EXPAND_ANYWAY",
		BUTTON_FREEZE: "FREEZE",
		BUTTON_GENERATE_ANYWAY: "GENERATE_ANYWAY",
		BUTTON_ISSUE: "ISSUE",
		BUTTON_KEEP_CURRENT_BASELINE: "KEEP_CURRENT_BASELINE",
		BUTTON_LEAVE_PAGE: "LEAVE_PAGE",
		BUTTON_MAKE_ACTIVE: "MAKE_ACTIVE",
		BUTTON_MAKE_INACTIVE: "MAKE_INACTIVE",
		BUTTON_MARK_FOR_DELETION: "MARK_FOR_DELETION",
		BUTTON_NOTIFY_COLLABORATORS: "NOTIFY_COLLABORATORS",
		BUTTON_PROCEED: "PROCEED",
		BUTTON_PURGE: "PURGE",
		BUTTON_PURGE_RESET: "PURGE_RESET",
		BUTTON_PUSH: "PUSH",
		BUTTON_REDO_ANYWAY: "REDO_ANYWAY",
		BUTTON_RUN: "RUN",
		BUTTON_UNLINK: "UNLINK",
		BUTTON_UNLINK_AND_DELETE: "UNLINK_AND_DELETE",
		BUTTON_UPDATE: "UPDATE",
		BUTTON_REFRESH: "REFRESH",
		BUTTON_REMOVE: "REMOVE",
		BUTTON_REOPEN: "REOPEN",
		BUTTON_RESET: "RESET",
		BUTTON_RESTORE: "RESTORE",
		BUTTON_RETRY: "RETRY",
		BUTTON_REVERT: "REVERT",
		BUTTON_SAVE: "SAVE",
		BUTTON_SEND_ANYWAY: "SEND_ANYWAY",
		BUTTON_START_IMMEDIATELY: "START_IMMEDIATELY",
		BUTTON_UNFREEZE: "UNFREEZE",
		BUTTON_UNMARK_FOR_DELETION: "UNMARK_FOR_DELETION",
		BUTTON_UPDATE_BASELINE: "UPDATE_BASELINE",

		// Button class.
		BUTTON_PRIMARY: _BUTTON_PRIMARY,

		// Defined Material Icons:
		ICON_ERROR: 'error',
		ICON_WARNING: 'warning',
		ICON_COMPLETED: 'check_circle',
		ICON_QUESTION: 'help',
		ICON_INFO: 'info'
	});

	return MessageDialog;
});